import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import { triggerEmail, EMAIL_TEMPLATES } from '../../utils/emailHelper';
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY, ADM_MODAL_FOOTER, ADM_MODAL_HEADER_DOT } from './admissionModalStyles';

const TEMPLATE_OPTIONS = [
  { key: 'TEST_ASSIGNED', label: 'Test Assigned' },
  { key: 'ADMISSION_CONFIRMED', label: 'Admission Confirmed' },
];

const SendEmailModal = ({ show, onHide, student, client }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (show) {
      setError(''); setSuccess(false); setSelectedTemplate('');
      setSubject("Update from Rizee College");
      setMessage(`Dear ${student?.student_name || 'Student'},\n\nThis is regarding your admission at Rizee College.`);
    }
  }, [show, student]);

  const handleTemplateChange = (e) => {
    const key = e.target.value;
    setSelectedTemplate(key);
    if (key && EMAIL_TEMPLATES[key]) {
      const tpl = EMAIL_TEMPLATES[key];
      setSubject(tpl.subject);
      setMessage(
        tpl.message
          .replace(/{student_name}/g, student?.student_name || 'Student')
          .replace(/{username}/g, student?.student_mobile || 'N/A')
          .replace(/{password}/g, student?.admission_test_password || '******')
      );
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !subject.trim()) return;
    setSending(true); setError('');
    const ok = await triggerEmail(client, {
      student, type: 'MANUAL',
      customData: { subject, message }
    });
    if (ok) { setSuccess(true); } else { setError("Failed to send. Please check server logs."); }
    setSending(false);
  };

  const handleClose = () => { setSuccess(false); setError(''); onHide(); };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" dialogClassName="af-premium-modal">
      <Modal.Header closeButton style={ADM_MODAL_HEADER}>
        <Modal.Title style={ADM_MODAL_TITLE}>
          <span style={ADM_MODAL_HEADER_DOT} />
          <i className="fal fa-envelope" />
          <span>
            Send Custom Email
            <span style={ADM_MODAL_TITLE_SUB}>Pick a template or compose your own message</span>
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ ...ADM_MODAL_BODY, maxHeight: '70vh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {error && (
          <div className="alert alert-danger d-flex align-items-center py-2 mb-3" style={{ borderRadius: '8px', fontSize: '13px' }}>
            <i className="fas fa-exclamation-circle mr-2" />{error}
          </div>
        )}

        {success ? (
          <div className="text-center py-4">
            <div style={{
              width: '64px', height: '64px', borderRadius: '18px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', boxShadow: '0 8px 20px rgba(16,185,129,0.3)'
            }}>
              <i className="fas fa-check" style={{ fontSize: '26px', color: '#fff' }} />
            </div>
            <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Email Sent!</h5>
            <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
              Email sent to <strong>{student?.student_name}</strong>
            </p>
            <button className="btn btn-outline-secondary" style={{ borderRadius: '10px', height: '40px', minWidth: '120px', fontWeight: '500' }} onClick={handleClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* ── Recipient ── */}
            <div className="af-modal-student-summary">
              <div className="af-summary-avatar">
                {(student?.student_name || 'S')[0].toUpperCase()}
              </div>
              <div className="af-summary-text">
                <strong>{student?.student_name}</strong>
                <span className="text-muted" style={{ fontSize: '13px' }}>
                  <i className="fas fa-envelope mr-1" />{student?.student_email || student?.email || '—'}
                </span>
              </div>
              <span style={{
                marginLeft: 'auto', padding: '4px 12px', borderRadius: '20px',
                background: '#fef2f2', color: '#dc2626',
                fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
              }}>
                <i className="fas fa-envelope" /> Email
              </span>
            </div>

            {/* ── Template + Subject ── */}
            <Row className="mb-3">
              <Col md={5}>
                <Form.Label className="af-field-label">Template (Optional)</Form.Label>
                <Form.Control
                  as="select" className="af-form-select"
                  value={selectedTemplate} onChange={handleTemplateChange}
                >
                  <option value="">— Custom Email —</option>
                  {TEMPLATE_OPTIONS.map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={7}>
                <Form.Label className="af-field-label">Subject</Form.Label>
                <Form.Control
                  type="text"
                  className="af-form-select"
                  placeholder="Email subject..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </Col>
            </Row>

            {/* ── Body ── */}
            <Form.Group className="mb-0">
              <Form.Label className="af-field-label">Message</Form.Label>
              <Form.Control
                as="textarea" rows={9} className="af-form-textarea"
                placeholder="Compose your email..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      {!success && (
        <Modal.Footer style={ADM_MODAL_FOOTER}>
          <button className="af-cancel-btn" onClick={handleClose}>Cancel</button>
          <button className="af-save-btn" onClick={handleSend} disabled={sending || !message.trim() || !subject.trim()} style={{ minWidth: '140px' }}>
            {sending
              ? <><Spinner animation="border" size="sm" className="mr-2" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />Sending...</>
              : <><i className="fas fa-paper-plane mr-2" />Send Email</>
            }
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default withApollo(SendEmailModal);
